import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Topbar from '../../components/common/Topbar';
import Toast from '../../components/common/Toast';
import { showToast } from '../../store/uiSlice';
import { fmtUSD } from '../../utils/formatters';
import api from '../../services/api';

const Credit = () => {
  const dispatch = useDispatch();
  const { balance } = useSelector((state) => state.trade);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [bankStatement, setBankStatement] = useState(null);
  const [panCard, setPanCard] = useState(null);
  const [cibil, setCibil] = useState(null);
  const [creditRequests, setCreditRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('request');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { fetchCreditRequests(); }, []);

  const fetchCreditRequests = async () => {
    try { const res = await api.get('/user/credit/requests'); setCreditRequests(res.data || []); } catch (e) {}
  };

  const handleSubmit = async () => {
    if (!amount || Number(amount) < 1) { dispatch(showToast({ message: 'Minimum credit amount is $1', type: 'error' })); return; }
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('amount', amount); formData.append('reason', reason);
      if (bankStatement) formData.append('bankStatement', bankStatement);
      if (panCard) formData.append('panCard', panCard);
      if (cibil) formData.append('cibilScreenshot', cibil);
      await api.post('/user/credit/request', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      dispatch(showToast({ message: 'Credit request submitted!', type: 'success' }));
      setAmount(''); setReason(''); setBankStatement(null); setPanCard(null); setCibil(null);
      fetchCreditRequests(); setActiveTab('history');
    } catch (e) { dispatch(showToast({ message: e.response?.data?.message || 'Failed', type: 'error' })); }
    finally { setIsSubmitting(false); }
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') return { text: 'Approved', bg: 'var(--color-greenBg)', color: 'var(--color-green)', border: 'var(--color-greenBorder)' };
    if (status === 'rejected') return { text: 'Rejected', bg: 'var(--color-redBg)', color: 'var(--color-red)', border: 'var(--color-redBorder)' };
    return { text: 'Pending', bg: 'var(--color-yellowBg)', color: 'var(--color-yellow)', border: 'var(--color-yellowBorder)' };
  };

  const inputStyle = { background: 'var(--color-bgInput)', border: '1px solid var(--color-borderMedium)', color: 'var(--color-text)', outline: 'none' };
  const cardStyle = { background: 'var(--color-bgSurface)', border: '1px solid var(--color-border)' };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg)' }}>
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute w-[700px] h-[700px] rounded-full top-[-260px] right-[-200px] animate-float" style={{ background: 'radial-gradient(circle, rgba(46,54,66,0.08), transparent 65%)' }} />
        <div className="absolute w-[600px] h-[600px] rounded-full bottom-[-200px] left-[-160px] animate-float animation-delay-2000" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.05), transparent 65%)' }} />
      </div>
      <Topbar />

      <div className="relative z-10 pt-[52px] p-5 flex items-center justify-center min-h-[calc(100vh-52px)]">
        <div className="w-full max-w-[700px]">
          <div className="text-center mb-8 animate-slide-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3" style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)' }}>
              <i className="fas fa-credit-card text-[9px]" style={{ color: 'var(--color-text)' }} />
              <span className="text-[9px] font-mono tracking-wide" style={{ color: 'var(--color-text)' }}>CREDIT</span>
            </div>
            <h1 className="font-syne text-3xl md:text-4xl font-extrabold mb-2" style={{ color: 'var(--color-text)' }}>
              Request <span style={{ color: 'var(--color-text)' }}>Credit</span>
            </h1>
            <p className="text-sm" style={{ color: 'var(--color-textSecondary)' }}>Apply for trading credit by submitting required documents</p>
          </div>

          <div className="rounded-2xl overflow-hidden animate-slide-up animation-delay-100" style={cardStyle}>
            <div className="flex items-center gap-3 px-6 py-4 border-b" style={{ background: 'var(--color-bgCard)', borderColor: 'var(--color-borderMedium)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}>
                <i className="fas fa-credit-card text-base" />
              </div>
              <div>
                <div className="font-syne text-base font-extrabold" style={{ color: 'var(--color-text)' }}>Credit Request</div>
                <div className="text-[9px] font-mono" style={{ color: 'var(--color-textDim)' }}>Submit documents for credit approval</div>
              </div>
            </div>

            <div className="flex border-b" style={{ borderColor: 'var(--color-borderMedium)' }}>
              {['request', 'history'].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className="flex-1 py-3 text-[11px] font-syne font-bold transition-all capitalize border-b-2"
                  style={{ color: activeTab === tab ? 'var(--color-text)' : 'var(--color-textSecondary)', borderColor: activeTab === tab ? 'var(--color-orange)' : 'transparent' }}>
                  {tab === 'request' ? 'New Request' : 'My Requests'}
                  {tab === 'history' && creditRequests.length > 0 && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'var(--color-bgCard)' }}>{creditRequests.length}</span>}
                </button>
              ))}
            </div>

            {activeTab === 'request' ? (
              <div className="p-6 space-y-5">
                <div className="text-center p-4 rounded-xl border" style={{ background: 'linear-gradient(to right, var(--color-orangeBg), var(--color-purpleBg))', borderColor: 'var(--color-orangeBorder)' }}>
                  <div className="text-[8px] font-mono tracking-wide uppercase mb-1" style={{ color: 'var(--color-textDim)' }}>Available Balance</div>
                  <div className="font-syne text-3xl font-extrabold" style={{ color: 'var(--color-text)' }}>{fmtUSD(balance)}</div>
                </div>

                <div>
                  <label className="text-[9px] font-mono tracking-wide uppercase block mb-2" style={{ color: 'var(--color-text)' }}>Credit Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 font-syne text-base font-extrabold" style={{ color: 'var(--color-text)' }}>$</span>
                    <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" min="1"
                      className="w-full rounded-lg px-3 py-3 pl-7 font-dm text-sm" style={inputStyle}
                      onFocus={e => e.target.style.borderColor = 'var(--color-orange)'}
                      onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'} />
                  </div>
                </div>

                <div>
                  <label className="text-[9px] font-mono tracking-wide uppercase block mb-2" style={{ color: 'var(--color-text)' }}>Reason for Credit</label>
                  <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="Explain why you need this credit..."
                    className="w-full rounded-lg px-3 py-3 font-dm text-sm resize-none" style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'var(--color-orange)'}
                    onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'} />
                </div>

                <div className="space-y-3">
                  {[
                    { label: 'Bank Statement', setter: setBankStatement },
                    { label: 'PAN Card', setter: setPanCard },
                    { label: 'CIBIL Score Screenshot', setter: setCibil },
                  ].map(field => (
                    <div key={field.label}>
                      <label className="text-[9px] font-mono tracking-wide uppercase block mb-2" style={{ color: 'var(--color-text)' }}>{field.label}</label>
                      <input type="file" onChange={e => field.setter(e.target.files[0])} accept=".jpg,.jpeg,.png,.pdf"
                        className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:cursor-pointer transition-all"
                        style={{ color: 'var(--color-textSecondary)' }}
                        onMouseEnter={e => e.target.style.color = 'var(--color-text)'}
                        onMouseLeave={e => e.target.style.color = 'var(--color-textSecondary)'} />
                    </div>
                  ))}
                </div>

                <button onClick={handleSubmit} disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-syne text-sm font-bold transition-all active:translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: 'var(--color-gradientOrange)', color: 'var(--color-textInverse)' }}>
                  {isSubmitting ? <><span className="inline-block w-3 h-3 border-2 rounded-full animate-spin mr-1" style={{ borderColor: 'var(--color-textInverse)', borderTopColor: 'transparent' }} /> Processing</> : <><i className="fas fa-paper-plane mr-1" /> Submit Credit Request</>}
                </button>
              </div>
            ) : (
              <div className="p-6">
                {creditRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="fas fa-credit-card text-3xl mb-3 block opacity-50" style={{ color: 'var(--color-textDim)' }} />
                    <p className="font-mono text-xs" style={{ color: 'var(--color-textDim)' }}>No credit requests yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {creditRequests.map(req => {
                      const badge = getStatusBadge(req.status);
                      return (
                        <div key={req.id} className="rounded-xl p-4" style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderMedium)' }}>
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-syne text-sm font-bold" style={{ color: 'var(--color-green)' }}>+{fmtUSD(req.amount)}</span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-mono border" style={{ background: badge.bg, color: badge.color, borderColor: badge.border }}>{badge.text}</span>
                          </div>
                          {req.reason && <div className="text-[10px] mb-2" style={{ color: 'var(--color-textMuted)' }}>{req.reason}</div>}
                          <div className="text-[8px] font-mono" style={{ color: 'var(--color-textDim)' }}>Requested: {new Date(req.createdAt).toLocaleString()}</div>
                          {req.status === 'rejected' && req.rejectionReason && <div className="mt-2 p-2 rounded-lg text-[9px] font-mono" style={{ background: 'var(--color-redBg)', border: '1px solid var(--color-redBorder)', color: 'var(--color-red)' }}>Rejected: {req.rejectionReason}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Toast />
      <style>{`
        @keyframes float { 0%,100% { transform:translate(0,0) } 50% { transform:translate(28px,-28px) } }
        @keyframes slide-up { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
        .animate-float { animation:float 20s ease-in-out infinite }
        .animation-delay-2000 { animation-delay:-8s }
        .animate-slide-up { animation:slide-up .6s ease-out both }
        .animation-delay-100 { animation-delay:.1s }
      `}</style>
    </div>
  );
};

export default Credit;